import React, { Fragment, PropsWithChildren, Suspense, useEffect } from 'react';
import { Button, Center, Heading, Text } from '@chakra-ui/react';
import { Navigate, Route, Routes } from 'react-router-dom';
import reactLazyWithRetry from '@fatso83/retry-dynamic-import/react-lazy';
import { useAppRoutes } from './useAppRoutes';
import { useSidebarOpenStore } from '../store';
import { Claim, ModulesEnum } from '../types';
import { getInitialPath, hasCommunityAccess } from '../utils';
import { LinkItems } from '../constants';
import { MyProfileRoutes } from '../pages/MyProfile/helper';
import {
	DashboardLayout,
	Loader,
	OnboardingGuard,
	ProtectedRoleArea,
	VideoInstructionModal,
} from '../components';
import { MainForm, NameForm, PhoneForm } from '../pages/MyProfile/components';

const PropertyLookup = reactLazyWithRetry(
	() => import('../pages/PropertyLookup/PropertyLookup'),
);
const DirectPage = reactLazyWithRetry(() =>
	import('../pages/Messenger/Direct/DirectPage').then(module => ({
		default: module.DirectPage,
	})),
);
const DirectMessages = reactLazyWithRetry(() =>
	import('../pages/Messenger/Direct/DirectMessages').then(module => ({
		default: module.DirectMessages,
	})),
);
const ServerPage = reactLazyWithRetry(() =>
	import('../pages/Messenger/Server/ServerPage').then(module => ({
		default: module.ServerPage,
	})),
);
const ServerMessages = reactLazyWithRetry(() =>
	import('../pages/Messenger/Server/ServerMessages').then(module => ({
		default: module.ServerMessages,
	})),
);
const VendorsPage = reactLazyWithRetry(() =>
	import('../pages/Vendors/Vendors').then(module => ({
		default: module.VendorsPage,
	})),
);
const VendorPage = reactLazyWithRetry(() =>
	import('../pages/Vendor/Vendor').then(module => ({
		default: module.VendorPage,
	})),
);
const CruzLayout = reactLazyWithRetry(() =>
	import('../modules/cruz-ctrl/components/Layout/Layout').then(module => ({
		default: module.CruzLayout,
	})),
);
const Courses = reactLazyWithRetry(() => import('../pages/Courses/Courses'));
const Course = reactLazyWithRetry(() => import('../pages/Course/Course'));
const CourseSection = reactLazyWithRetry(
	() => import('../pages/CourseSection/CourseSection'),
);
const QuickStart = reactLazyWithRetry(
	() => import('../pages/QuickStart/QuickStart'),
);
const LlcVault = reactLazyWithRetry(() => import('../pages/LlcVault/LlcVault'));
const CityResearch = reactLazyWithRetry(
	() => import('../pages/CityResearch/CityResearch'),
);
const CrimeLookup = reactLazyWithRetry(
	() => import('../pages/CrimeLookup/CrimeLookup'),
);
const DealCalculator = reactLazyWithRetry(
	() => import('../pages/DealCalculator/DealCalculator'),
);
const DealExplorer = reactLazyWithRetry(
	() => import('../pages/DealExplorer/DealExplorer'),
);
const DealExplorerRecommendations = reactLazyWithRetry(
	() => import('../pages/DealExplorer/DealExplorerRecommendations'),
);
const FMRExplorer = reactLazyWithRetry(
	() => import('../pages/FMRExplorer/FMRExplorer'),
);
const MessengerLayout = reactLazyWithRetry(
	() => import('../pages/Messenger/MessengerLayout'),
);
const Forbidden = reactLazyWithRetry(() => import('../pages/403/Forbidden'));
const CommunityInstruction = reactLazyWithRetry(
	() => import('../pages/CommunityInstruction/CommunityInstruction'),
);
const TenantFinder = reactLazyWithRetry(
	() => import('../pages/TenantFinder/TenantFinder'),
);
const AiChatLayout = reactLazyWithRetry(() =>
	import('../modules/aiAssistant/pages/AiAssistantPage').then(module => ({
		default: module.AiChatLayout,
	})),
);
const AiChatView = reactLazyWithRetry(() =>
	import('../modules/aiAssistant/pages/AiAssistantPage').then(module => ({
		default: module.AiChatView,
	})),
);
const Dashboard = reactLazyWithRetry(
	() => import('../modules/cruz-ctrl/pages/Dashboard/Dashboard'),
);
const Properties = reactLazyWithRetry(
	() => import('../modules/cruz-ctrl/pages/Properties/Properties'),
);
const Tenants = reactLazyWithRetry(
	() => import('../modules/cruz-ctrl/pages/Tenants/Tenants'),
);
const Invoices = reactLazyWithRetry(
	() => import('../modules/cruz-ctrl/pages/Invoices/Invoices'),
);
const Planner = reactLazyWithRetry(
	() => import('../modules/cruz-ctrl/pages/Planner/Planner'),
);

const Property = reactLazyWithRetry(
	() => import('../modules/cruz-ctrl/pages/Property/Property'),
);

const BusinessEntity = reactLazyWithRetry(
	() => import('../modules/cruz-ctrl/pages/BusinessEntity/BusinessEntity'),
);
const DataSource = reactLazyWithRetry(
	() => import('../modules/cruz-ctrl/pages/DataSource/DataSource'),
);

const Users = reactLazyWithRetry(
	() => import('../modules/cruz-ctrl/pages/Users/Users'),
);

const Tenant = reactLazyWithRetry(
	() => import('../modules/cruz-ctrl/pages/Tenant/Tenant'),
);

const SuspenseLoader = (
	<Loader variant="brand" centerProps={{ minH: '100dvh' }} />
);

export const AppRoutes = () => {
	const {
		isGlobalLoading,
		bootstrapFailed,
		needsOnboarding,
		isCampaignFieldsSet,
		claimsIds,
		modulesIds,
		isLimitedAccess,
		isAdmin,
		isShowQuickStart,
	} = useAppRoutes();

	if (isGlobalLoading) {
		return (
			<Loader
				variant="brand"
				label="Loading Section 8 Pro…"
				centerHeight="100dvh"
			/>
		);
	}

	if (bootstrapFailed) {
		return (
			<Center
				minH="100dvh"
				px={6}
				flexDirection="column"
				textAlign="center">
				<Heading as="h1" size="lg">
					We could not load the app
				</Heading>
				<Text mt={3} maxW="520px">
					Please reload the page and try again.
				</Text>
				<Button mt={6} onClick={() => window.location.reload()}>
					Reload page
				</Button>
			</Center>
		);
	}

	if (needsOnboarding) {
		return <OnboardingGuard isCampaignFieldsSet={false} />;
	}

	return (
		<Fragment>
			<RoutesContainer
				isCampaignFieldsSet={isCampaignFieldsSet}
				claimsIds={claimsIds}>
				<Routes>
					<Route path="/" element={<DashboardLayout />}>
						<Route
							path="/"
							element={
								claimsIds?.length ? (
									<Navigate
										to={getInitialPath({
											isLimitedAccess,
											claimsIds,
											items: LinkItems,
										})}
										replace={true}
									/>
								) : (
									<div>root</div>
								)
							}
						/>
						<Route path="/forbidden" element={<Forbidden />} />
						<Route
							path="fmr-explorer"
							element={
								<ProtectedRoleArea
									isAllowed={
										!!claimsIds?.includes(
											Claim['FMR Explorer'],
										)
									}>
									<FMRExplorer />
								</ProtectedRoleArea>
							}
						/>
						<Route
							path="courses"
							element={
								<ProtectedRoleArea
									isAllowed={
										!!claimsIds?.includes(Claim.Courses)
									}>
									<Courses />
								</ProtectedRoleArea>
							}
						/>

						<Route
							path={'/quick-start'}
							element={
								<ProtectedRoleArea isAllowed={isShowQuickStart}>
									<QuickStart />
								</ProtectedRoleArea>
							}
						/>
						<Route
							path="llc-vault"
							element={
								<ProtectedRoleArea isAllowed={true}>
									<LlcVault />
								</ProtectedRoleArea>
							}
						/>
						<Route
							path="city-research"
							element={
								<ProtectedRoleArea
									isAllowed={
										!!claimsIds?.includes(
											Claim['City Research'],
										)
									}>
									<CityResearch />
								</ProtectedRoleArea>
							}
						/>
						<Route
							path="courses/:id"
							element={
								<ProtectedRoleArea
									isAllowed={
										!!claimsIds?.includes(Claim.Courses)
									}>
									<Course />
								</ProtectedRoleArea>
							}
						/>
						<Route
							path="courses/:courseId/:sectionId"
							element={
								<ProtectedRoleArea
									isAllowed={
										!!claimsIds?.includes(Claim.Courses)
									}>
									<CourseSection />
								</ProtectedRoleArea>
							}
						/>
						<Route
							path="property-lookup"
							element={
								<ProtectedRoleArea
									isAllowed={
										!!claimsIds?.includes(
											Claim['Property Lookup'],
										)
									}>
									<PropertyLookup />
								</ProtectedRoleArea>
							}
						/>
						<Route
							path="crime-lookup"
							element={
								<ProtectedRoleArea
									isAllowed={
										!!claimsIds?.includes(
											Claim['Property Lookup'],
										) ||
										!!claimsIds?.includes(
											Claim['Deal Explorer'],
										)
									}>
									<CrimeLookup />
								</ProtectedRoleArea>
							}
						/>
						<Route
							path={MyProfileRoutes.MY_PROFILE}
							element={<MainForm />}
						/>
						<Route
							path={
								MyProfileRoutes.MY_PROFILE +
								'/' +
								MyProfileRoutes.NAME
							}
							element={<NameForm />}
						/>
						<Route
							path={
								MyProfileRoutes.MY_PROFILE +
								'/' +
								MyProfileRoutes.PHONE
							}
							element={<PhoneForm />}
						/>
						<Route
							path="deal-calculator"
							element={
								<ProtectedRoleArea
									isAllowed={
										!!claimsIds?.includes(
											Claim['Deal Calculator'],
										)
									}>
									<DealCalculator />
								</ProtectedRoleArea>
							}
						/>
						<Route
							path="deal-explorer"
							element={
								<ProtectedRoleArea
									isAllowed={
										!!claimsIds?.includes(
											Claim['Deal Explorer'],
										)
									}>
									<DealExplorer />
								</ProtectedRoleArea>
							}
						/>
						<Route
							path="deal-explorer/recommendations"
							element={
								<ProtectedRoleArea
									isAllowed={
										!!claimsIds?.includes(
											Claim['Deal Explorer'],
										) && !isLimitedAccess
									}>
									<DealExplorerRecommendations />
								</ProtectedRoleArea>
							}
						/>

						<Route
							path="messenger"
							element={
								<ProtectedRoleArea
									isAllowed={
										hasCommunityAccess(claimsIds) &&
										modulesIds.includes(
											ModulesEnum.ExpertNetwork,
										)
									}>
									<MessengerLayout />
								</ProtectedRoleArea>
							}>
							<Route
								path="direct"
								element={
									<Suspense fallback={SuspenseLoader}>
										<DirectPage />
									</Suspense>
								}>
								<Route
									path=":id"
									element={
										<Suspense fallback={SuspenseLoader}>
											<DirectMessages />
										</Suspense>
									}
								/>
							</Route>
							<Route
								path="community"
								element={
									<Suspense fallback={SuspenseLoader}>
										<ServerPage />
									</Suspense>
								}>
								<Route
									path=":id"
									element={
										<Suspense fallback={SuspenseLoader}>
											<ServerMessages />
										</Suspense>
									}
								/>
							</Route>
						</Route>

						<Route
							path="tenant-finder"
							element={
								<ProtectedRoleArea
									isAllowed={
										!!claimsIds?.includes(
											Claim.TenantFinder,
										) && isAdmin
									}>
									<TenantFinder />
								</ProtectedRoleArea>
							}
						/>

						<Route
							path="/vendors"
							element={
								<ProtectedRoleArea
									isAllowed={
										!!claimsIds?.includes(Claim.Vendors) &&
										modulesIds.includes(
											ModulesEnum.ExpertNetwork,
										)
									}>
									<VendorsPage />
								</ProtectedRoleArea>
							}
						/>
						<Route
							path="/vendor"
							element={
								<ProtectedRoleArea
									isAllowed={
										!!claimsIds?.includes(Claim.Vendors) &&
										modulesIds.includes(
											ModulesEnum.ExpertNetwork,
										)
									}>
									<VendorPage />
								</ProtectedRoleArea>
							}
						/>

						<Route
							path="/instruction"
							element={
								<Suspense>
									<CommunityInstruction />
								</Suspense>
							}
						/>
						{/*	AI ASSISTANT*/}
						<Route
							path={'ai-assistant'}
							element={
								<ProtectedRoleArea isAllowed={isAdmin}>
									<AiChatLayout />
								</ProtectedRoleArea>
							}>
							<Route path={':chatId?'} element={<AiChatView />} />
						</Route>
					</Route>
					<Route
						path={'/cruz-ctrl'}
						element={
							<ProtectedRoleArea
								isAllowed={
									!!claimsIds?.includes(Claim.CruzControl) &&
									modulesIds?.includes(ModulesEnum.DealTools)
								}>
								<CruzLayout />
							</ProtectedRoleArea>
						}>
						<Route path={'dashboard'} element={<Dashboard />} />
						<Route path={'properties'} element={<Properties />} />
						<Route path={'tenants'} element={<Tenants />} />
						<Route path={'invoices'} element={<Invoices />} />
						<Route path={'planner'} element={<Planner />} />
						<Route path={'property/:id'} element={<Property />} />
						<Route
							path={'business-entity'}
							element={<BusinessEntity />}
						/>
						<Route path={'data-source'} element={<DataSource />} />
						<Route path={'users'} element={<Users />} />
						<Route
							path={'tenants/:tenantId'}
							element={<Tenant />}
						/>
					</Route>
				</Routes>
			</RoutesContainer>
			<VideoInstructionModal />
		</Fragment>
	);
};

const RoutesContainer = ({
	isCampaignFieldsSet,
	claimsIds,
	children,
}: PropsWithChildren<{
	isCampaignFieldsSet?: boolean;
	claimsIds?: Array<Claim>;
}>) => {
	const { onOpen } = useSidebarOpenStore();

	useEffect(() => {
		if (isCampaignFieldsSet && hasCommunityAccess(claimsIds)) {
			onOpen();
		}
	}, [isCampaignFieldsSet, claimsIds]);

	return <>{children}</>;
};
